// RPT2 (docs/build-briefs/RPT2-menu-profitability.md; PRD P0.13; design/mockups/rpt-menu-profitability/): the pure,
// DOM-free part of the Menu profitability screen (ui/src/menuProfitability.ts), kept apart so a plain node:test file
// can import it (test/rpt2-menu-profitability-ui-model.test.ts). Sorting, the quadrant filter, the wording of every
// figure and the stepper's bounds live here. The server decides every figure; nothing here recomputes one.

import type { MenuProfitabilityDrinkDto, MenuProfitabilityQuadrant } from "./api.ts";
import { formatMoney } from "./reportFormat.ts";

export const MP_QUADRANTS: ReadonlyArray<{
  id: MenuProfitabilityQuadrant;
  label: string;
  /** The singular used on a drink's pill. */
  pill: string;
  detail: string;
  tone: "ok" | "amb" | "inf" | "red";
}> = [
  { id: "star", label: "Stars", pill: "Stars", detail: "High sales, high margin", tone: "ok" },
  {
    id: "workhorse",
    label: "Workhorses",
    pill: "Workhorses",
    detail: "High sales, low margin",
    tone: "amb",
  },
  {
    id: "puzzle",
    label: "Puzzles",
    pill: "Puzzles",
    detail: "Low sales, high margin",
    tone: "inf",
  },
  { id: "drag", label: "Drags", pill: "Drags", detail: "Low sales, low margin", tone: "red" },
];

export type MpSort = "totalMargin" | "units" | "pourCost";

export const MP_SORTS: ReadonlyArray<{ id: MpSort; label: string }> = [
  { id: "totalMargin", label: "Total margin" },
  { id: "units", label: "Units" },
  { id: "pourCost", label: "Pour cost" },
];

/** The chips the report offers, in the mockup's order. */
export const MP_PRESETS: ReadonlyArray<{ id: "today" | "thisWeek" | "thisMonth"; label: string }> =
  [
    { id: "today", label: "Today" },
    { id: "thisWeek", label: "This week" },
    { id: "thisMonth", label: "This month" },
  ];

export const MP_DEFAULT_PRESET = "thisWeek" as const;

/** Whole dollars, for revenue and total margin: "$1,222". */
export function wholeDollars(cents: number | null): string {
  if (cents === null) return "-";
  const rounded = Math.round(cents / 100);
  return formatMoney(rounded * 100).replace(/\.00$/, "");
}

/** Dollars and cents, for a cost, a margin a drink and a price: "$2.60". */
export function dollars(cents: number | null): string {
  return cents === null ? "-" : formatMoney(cents);
}

/** A whole percent from basis points: 2308 is "23%". */
export function percent(basisPoints: number | null): string {
  return basisPoints === null ? "-" : `${Math.round(basisPoints / 100)}%`;
}

/** The drink's quadrant pill label, or null for a drink outside the quadrants. */
export function quadrantLabel(quadrant: MenuProfitabilityQuadrant | null): string | null {
  return MP_QUADRANTS.find((q) => q.id === quadrant)?.pill ?? null;
}

/** The pour cost reads as over target when the drink is costed and not on target. */
export function isOverTarget(drink: MenuProfitabilityDrinkDto): boolean {
  return drink.costed && drink.onTarget === false;
}

/**
 * The visible rows: an uncosted drink is never in a quadrant, so a quadrant filter hides it; with no filter every
 * drink shows. Sorted by the chosen column, best first (pour cost: highest first, because that is the drink to look
 * at); an uncosted drink is last under every sort, more units first.
 */
export function visibleDrinks(
  drinks: readonly MenuProfitabilityDrinkDto[],
  sort: MpSort,
  quadrant: MenuProfitabilityQuadrant | null,
): MenuProfitabilityDrinkDto[] {
  const shown = quadrant === null ? [...drinks] : drinks.filter((d) => d.quadrant === quadrant);
  const key = (d: MenuProfitabilityDrinkDto): number =>
    sort === "units"
      ? d.sold
      : sort === "pourCost"
        ? (d.pourCostBp ?? 0)
        : (d.totalMarginCents ?? 0);
  return shown.sort((a, b) => {
    if (a.costed !== b.costed) return a.costed ? -1 : 1;
    if (!a.costed) return b.sold - a.sold || a.name.localeCompare(b.name);
    return key(b) - key(a) || b.sold - a.sold || a.name.localeCompare(b.name);
  });
}

/** A quadrant tile's tap: tapping the one already chosen clears it. */
export function nextQuadrantFilter(
  current: MenuProfitabilityQuadrant | null,
  tapped: MenuProfitabilityQuadrant,
): MenuProfitabilityQuadrant | null {
  return current === tapped ? null : tapped;
}

/** The stepper's next value, or null when the tap would leave the bounds (the button is disabled there). */
export function steppedTarget(
  current: number,
  direction: 1 | -1,
  bounds: { minBp: number; maxBp: number; stepBp: number },
): number | null {
  const next = current + direction * bounds.stepBp;
  return next < bounds.minBp || next > bounds.maxBp ? null : next;
}

/** The target as the stepper shows it: 2000 is "20%". */
export function targetText(basisPoints: number): string {
  return `${basisPoints / 100}%`;
}

/** The price cell: "On target" is a pill, a price is a dollar figure, N/A is N/A. */
export function priceCell(drink: MenuProfitabilityDrinkDto): {
  kind: "pill" | "price" | "na";
  text: string;
} {
  if (!drink.costed) return { kind: "na", text: "-" };
  if (drink.onTarget === true) return { kind: "pill", text: "On target" };
  return { kind: "price", text: dollars(drink.priceToHitTargetCents) };
}
